import { createSelector } from "reselect";
import { getMock } from "./mockSelectors";
import { getReplies } from "./replySelectors";

const getAnswers = (state) => state.entities.answers;
const getProps = (_, props) => props;

export const getAnswer = createSelector(
  getAnswers,
  getProps,
  (answers, props) =>
    Object.values(answers).filter(
      (answer) =>
        (props?.lessonId
          ? answer.lessonId === props.lessonId
          : answer.mockId === props.mockId) &&
        answer.questionId === props.questionId
    )[0]
);

export const getReplyAnswered = createSelector(
  getAnswer,
  getReplies,
  (answer, replies) => replies[answer?.replyId]
);

export const getAnswersFromMock = createSelector(
  getAnswers,
  getMock,
  (answers, mock) =>
    Object.values(answers).filter((answer) => answer.mockId === mock?.id)
);

export const getMockAnswersByValue = createSelector(
  getAnswersFromMock,
  getReplies,
  getProps,
  (answers, replies, value = true) => {
    return answers.filter((answer) => {
      if (typeof answer?.correct === "boolean") {
        return answer.correct === value;
      }

      const reply = replies[answer.replyId];
      return reply?.value === value;
    });
  }
);

export const getAnswerForQuestionStem = createSelector(
  (state) => state.entities?.answers || {},
  getProps,
  (answers, props) => {
    if (!props) return null;
    if (!answers || Object.keys(answers).length === 0) return null;

    const answersArray = Array.isArray(answers) ? answers : Object.values(answers);

    const matchingAnswer = answersArray.find(
      (answer) => {
        if (!answer) return false;

        const mockIdMatch = answer.mockId != null &&
          props.mockId != null &&
          Number(answer.mockId) === Number(props.mockId);
        const questionIdMatch = answer.questionId != null &&
          props.questionId != null &&
          Number(answer.questionId) === Number(props.questionId);
        const stemMatches = answer.questionStemId != null &&
          props.questionStemId != null &&
          Number(answer.questionStemId) === Number(props.questionStemId);

        return mockIdMatch && questionIdMatch && stemMatches;
      }
    );

    return matchingAnswer || null;
  }
);


export const getChoiceAnswered = createSelector(
  getAnswerForQuestionStem,
  (state) => state.entities.questionStems || {},
  getProps,
  (answer, stems, props) => {
    if (!answer) return null;
    if (!props) return null;

    const stem = stems[props.questionStemId] || Object.values(stems).find(
      s => Number(s.id) === Number(props.questionStemId)
    );
    if (!stem) return null;

    return stem.choices?.find((choice) => Number(choice.id) === Number(answer.choiceId));
  }
);

export const getStemAnswered = createSelector(
  getAnswerForQuestionStem,
  (state) => state.entities.questionStems || {},
  getProps,
  (answer, stems, props) => {
    if (!answer) return null;
    if (!props) return null;

    const stem = stems[props.questionStemId] || Object.values(stems).find(
      s => Number(s.id) === Number(props.questionStemId)
    );
    if (!stem) return null;

    const choice = stem.choices?.find((choice) => Number(choice.id) === Number(answer.choiceId));
    if (!choice) return null;

    return { value: choice.correct };
  }
);

export const getAnswerForLessonQuestionStem = createSelector(
  (state) => state.entities?.answers || {},
  getProps,
  (answers, props) => {
    if (!props) return null;
    if (!answers || Object.keys(answers).length === 0) return null;

    const answersArray = Array.isArray(answers) ? answers : Object.values(answers);

    const answerExists = answersArray.find(
      (answer) => {
        if (!answer) return false;

        const lessonMatches = answer.lessonId != null &&
          props.lessonId != null &&
          Number(answer.lessonId) === Number(props.lessonId);
        const questionMatches = answer.questionId != null &&
          props.questionId != null &&
          Number(answer.questionId) === Number(props.questionId);
        const stemIdMatch = answer.questionStemId != null &&
          props.questionStemId != null &&
          Number(answer.questionStemId) === Number(props.questionStemId);

        return lessonMatches && questionMatches && stemIdMatch;
      }
    );

    return answerExists || null;
  }
);

export const getAnswerForInteractivePrompt = createSelector(
  (state) => state.entities?.answers || {},
  getProps,
  (answers, props) => {
    if (!props) return null;
    if (!answers || Object.keys(answers).length === 0) return null;

    const answersArray = Array.isArray(answers) ? answers : Object.values(answers);

    return (
      answersArray.find((answer) => {
        if (!answer) return false;

        const questionMatches =
          answer.questionId != null &&
          props.questionId != null &&
          Number(answer.questionId) === Number(props.questionId);
        const promptMatches =
          answer.questionPromptId != null &&
          props.questionPromptId != null &&
          Number(answer.questionPromptId) === Number(props.questionPromptId);

        if (!questionMatches || !promptMatches) return false;

        if (props.lessonId != null) {
          return (
            answer.lessonId != null &&
            Number(answer.lessonId) === Number(props.lessonId)
          );
        }

        if (props.mockId != null) {
          return (
            answer.mockId != null &&
            Number(answer.mockId) === Number(props.mockId)
          );
        }

        return false;
      }) || null
    );
  }
);

export const getLessonQuestionAnswered = createSelector(
  (state) => state.entities?.answers || {},
  (state) => state.entities.questionStems || {},
  (state) => state.entities.replies || {},
  getProps,
  (answers, stems, replies, props) => {
    const question = props.questionItem?.question || props.question;
    const stem = props.questionItem?.stem || props.stem;
    const lessonId = props.lessonId;

    if (!question || !lessonId) return null;

    if (stem) {
      const answersArray = Array.isArray(answers) ? answers : Object.values(answers);
      const answer = answersArray.find(
        (ans) => {
          if (!ans) return false;
          const lessonMatches = ans.lessonId != null && Number(ans.lessonId) === Number(lessonId);
          const questionMatches = ans.questionId != null && Number(ans.questionId) === Number(question.id);
          const stemMatches = ans.questionStemId != null && Number(ans.questionStemId) === Number(stem.id);
          return lessonMatches && questionMatches && stemMatches;
        }
      );

      if (!answer) return null;

      const stemObj = stems[stem.id] || Object.values(stems).find(
        item => Number(item.id) === Number(stem.id)
      );
      if (!stemObj?.choices) return null;

      const choice = stemObj.choices.find(
        item => Number(item.id) === Number(answer.choiceId)
      );
      return choice ? { value: choice.correct } : null;
    }

    const answersArray = Array.isArray(answers) ? answers : Object.values(answers);
    const answer = answersArray.find(
      (ans) => {
        if (!ans) return false;
        const lessonMatches = ans.lessonId != null && Number(ans.lessonId) === Number(lessonId);
        const questionMatches = ans.questionId != null && Number(ans.questionId) === Number(question.id);
        return lessonMatches && questionMatches;
      }
    );

    if (!answer) return null;

    const reply = replies[answer.replyId];
    return reply || null;
  }
);

export const getAllStemsAnswered = createSelector(
  (state) => state.entities?.answers || {},
  (state) => state.entities?.questionStems || {},
  getProps,
  (answers, allStems, props) => {
    const { question, mockId, lessonId } = props || {};

    const stems = Object.values(allStems || {})
      .filter((stem) => {
        const stemQuestionId = stem.questionId || stem.question_id;
        return stemQuestionId != null && Number(stemQuestionId) === Number(question.id);
      })
      .sort((a, b) => (a.position || 0) - (b.position || 0));
    
    if (!stems || stems.length === 0) {
      return true;
    }

    const answersArray = Array.isArray(answers) ? answers : Object.values(answers);
    
    return stems.every((stem) => {
      return answersArray.some((answer) => {
        if (!answer) return false;
        
        const mockIdMatch = answer.mockId != null &&
          mockId != null &&
          Number(answer.mockId) === Number(mockId);
        const lessonIdMatch = answer.lessonId != null &&
          lessonId != null &&
          Number(answer.lessonId) === Number(lessonId);
        const questionIdMatch = answer.questionId != null &&
          question.id != null &&
          Number(answer.questionId) === Number(question.id);
        const stemMatches = answer.questionStemId != null &&
          stem.id != null &&
          Number(answer.questionStemId) === Number(stem.id);

        return (mockIdMatch || lessonIdMatch) && questionIdMatch && stemMatches;
      });
    });
  }
);
